import { makeStyles } from "tss-react/mui";
import * as mdi from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { MiIcon } from "../../MiIcon/MiIcon";

/**
 * MiFilterItem component renders a filter item with optional icon and right action.
 *
 * @param {Object} props - The component props.
 * @param {string} props.keyVal - The key value for the item.
 * @param {string} props.title - The title text for the item.
 * @param {function} props.handleClick - The click handler function.
 * @param {Object} [props.typographyStyle={}] - The style object for the Typography component.
 * @param {string} [props.typographyVariant="body2"] - The variant for the Typography component.
 * @param {boolean} [props.showIcon] - Flag to show or hide the icon.
 * @param {string} [props.iconPath] - The path for the icon from mdi.
 * @param {string} [props.iconColor] - The color for the icon.
 * @param {React.ReactNode} props.rightAction - The right action element to be displayed.
 * @param {boolean} [props.selected=false] - Flag to indicate if the item is selected.
 * @param {boolean} [props.disabled=false] - Flag to indicate if the item is disabled.
 * @returns {JSX.Element} The rendered MiFilterItem component.
 */
export function MiFilterItem({
  keyVal,
  title,
  handleClick,
  typographyStyle = {},
  typographyVariant = "body2",
  showIcon,
  iconPath,
  iconColor,
  rightAction,
  selected = false,
  disabled = false,
}) {
  const { classes } = useStyles();
  const containerStyles = `${classes.listItemContainer} ${
    disabled ? classes.disabled : ""
  } ${selected ? classes.selected : ""}`;
  return (
    <Box className={containerStyles} onClick={handleClick} key={keyVal}>
      <Typography sx={{ ...typographyStyle }} variant={typographyVariant}>
        {title}
      </Typography>
      {rightAction}
      {showIcon && <MiIcon path={mdi[iconPath]} size={0.8} color={iconColor} />}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  listItemContainer: {
    minHeight: 36,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
    padding: theme.spacing(0, 2),
    cursor: "pointer",
    userSelect: "none",
    "&:hover": {
      backgroundColor: theme.palette.grey.light,
    },
    "& p": { fontSize: theme.fontSize.medium },
  },
  disabled: {
    mouseEvents: "none",
    cursor: "default",
    "&:hover": {
      backgroundColor: theme.palette.grey.light,
    },
  },
  selected: {
    backgroundColor: theme.palette.primary.light,
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));
